import { Box } from '@withjoy/joykit';
import type { GalleryAsset } from './GenericPreviewPane';
import { AssetContainer } from './GenericPreviewPane';
import React, { useMemo } from 'react';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useTranslation } from '@shared/core';
import type { QuickTranslate } from '@shared/core/i18n/useTranslation';
import { PDP_SUPPLEMENTARY_URLS, getSupplementaryImageSizeKey } from '@apps/card/constants/pdpSupplementaryImages';

type Vendor = 'taylor' | 'joy_digital';

type PreviewPaneAsset = {
  categoryList: string[];
  type: 'image' | 'video';
  label?: string;
  key: string;
  src: string;
  thumbnailUrl?: string;
  /** Card sizes (orientation-agnostic, e.g. '4x6', '5x7') this asset is shown for.
   * Omitted = every size. Postcards select on their physical '4x6' size. */
  sizeList?: string[];
};

const getPreviewPaneAssets = (tq: QuickTranslate): PreviewPaneAsset[] => {
  const u = PDP_SUPPLEMENTARY_URLS;
  return [
    {
      key: 'cardEditor',
      src: u.DIGITAL_CARD_EDITOR,
      type: 'image',
      categoryList: ['*/*/joy_digital'],
    },
    {
      key: 'smsDelivery',
      src: u.DIGITAL_SMS_DELIVERY,
      type: 'image',
      categoryList: ['*/*/joy_digital'],
    },
    {
      key: 'cardBack',
      src: u.BACK_THANK_YOU,
      type: 'image',
      label: tq`Back of the Card`,
      categoryList: ['*/thankYou/taylor'],
      sizeList: ['5x7'],
    },
    {
      key: 'cardBack',
      src: u.BACK_THANK_YOU_POSTCARD,
      type: 'image',
      label: tq`Back of the Card`,
      categoryList: ['*/thankYou/taylor'],
      sizeList: ['4x6'],
    },
    {
      key: 'returnAddressing',
      src: u.ENVELOPES_COMMON,
      type: 'image',
      label: tq`Free Recipient & Return Addressing`,
      categoryList: ['*/thankYou/taylor'],
      sizeList: ['5x7'],
    },
    {
      key: 'envelope',
      src: u.ENVELOPE_THANK_YOU,
      type: 'image',
      label: tq`Envelope`,
      categoryList: ['*/thankYou/taylor'],
      sizeList: ['5x7'],
    },
    {
      key: 'cardBack',
      src: u.BACK_HOLIDAY,
      type: 'image',
      label: tq`Back of the Card`,
      categoryList: ['*/holiday/taylor'],
    },
    {
      key: 'returnAddressing',
      src: u.ENVELOPES_COMMON,
      type: 'image',
      label: tq`Free Recipient & Return Addressing`,
      categoryList: ['*/holiday/taylor'],
    },
    {
      key: 'envelope',
      src: u.ENVELOPE_HOLIDAY,
      type: 'image',
      label: tq`Envelope`,
      categoryList: ['*/holiday/taylor'],
    },
    {
      key: 'cardBack',
      src: u.BACK_SAVE_DATE_INVITATION_ENCLOSURE,
      type: 'image',
      label: tq`Back of the Card`,
      categoryList: ['*/saveTheDate/taylor', '*/invitation/taylor', '*/enclosure/taylor'],
      sizeList: ['5x7'],
    },
    {
      key: 'cardBack',
      src: u.BACK_SAVE_DATE_POSTCARD,
      type: 'image',
      label: tq`Back of the Card`,
      categoryList: ['*/saveTheDate/taylor'],
      sizeList: ['4x6'],
    },
    {
      key: 'returnAddressing',
      src: u.ENVELOPES_COMMON,
      type: 'image',
      label: tq`Free Recipient & Return Addressing`,
      categoryList: ['*/saveTheDate/taylor', '*/invitation/taylor'],
      sizeList: ['5x7'],
    },
    {
      key: 'envelope',
      src: u.ENVELOPE_SAVE_DATE_INVITATION,
      type: 'image',
      label: tq`Envelope`,
      categoryList: ['*/saveTheDate/taylor', '*/invitation/taylor'],
      sizeList: ['5x7'],
    },
  ];
};

const validatePreviewPaneAssetFilters = (asset: PreviewPaneAsset, occasion: string, category: StationeryTemplateCategoryEnum, vendor: Vendor): boolean => {
  return asset.categoryList.some(filterString => {
    const filterStringComponents = filterString.split('/');
    const [filterOccasion, filterCategory, filterVendor] = filterStringComponents;

    // Not valid if occasions do not match.
    if (filterOccasion !== '*' && filterOccasion !== occasion) {
      return false;
    }

    // Not valid if categories do not match.
    if (filterCategory !== '*' && filterCategory !== category) {
      return false;
    }

    // Not valid if vendors do not match.
    if (filterVendor !== '*' && filterVendor !== vendor) {
      return false;
    }

    return true;
  });
};

// Filters to assets for current product only.
const filterPreviewPaneAssets = (assets: PreviewPaneAsset[], occasion: string, category: StationeryTemplateCategoryEnum, vendor: Vendor, sizeKey?: string): PreviewPaneAsset[] => {
  return assets.filter(asset => {
    // Additive size selection: an asset with a sizeList shows only at those sizes.
    // Cards whose size can't be determined keep every asset (existing behavior).
    if (asset.sizeList && sizeKey && !asset.sizeList.includes(sizeKey)) return false;
    return validatePreviewPaneAssetFilters(asset, occasion, category, vendor);
  });
};

// Convert asset entries to usable GalleryAsset objects.
const mapAssetsToGalleryAssetObjects = (assets: PreviewPaneAsset[]): GalleryAsset[] => {
  return assets.map(asset => {
    if (asset.type === 'video' && asset.thumbnailUrl) {
      return {
        render: () => (
          <AssetContainer
            label={asset.label ?? ''}
            key={`video-${asset.key}`}
            enforceAspectRatio
          >
            <Box
              top={0}
              left={0}
              width="100%"
              height="100%"
              position="absolute"
              backgroundPosition="center"
              backgroundSize="cover"
              backgroundRepeat="no-repeat"
              backgroundImage={`url('${asset.thumbnailUrl}')`}
            >
              <Box
                as="iframe"
                border="none"
                outline="none"
                width="100%"
                height="100%"
                position="absolute"
                top={0}
                left={0}
                loading="lazy"
                src={`${asset.src}&autoplay=1&controls=0&loop=1&mute=1&background=1`}
              />
            </Box>
          </AssetContainer>
        ),
        thumbnailUrl: asset.thumbnailUrl,
      };
    }
    return {
      render: () => (
        <AssetContainer
          enforceAspectRatio
          key={asset.key}
          label={asset.label ?? ''}
          backgroundImageUrl={asset.src}
        />
      ),
      thumbnailUrl: asset.src,
    };
  });
};

export const useCardPreviewPaneAssets = (
  occasion: string,
  category: StationeryTemplateCategoryEnum,
  vendor: Vendor,
  // Pass primitives, not an inline object: a fresh object per render would bust the memo.
  card?: { widthInches?: number; heightInches?: number; productType?: string },
): GalleryAsset[] => {
  const { tq } = useTranslation('stationery');
  const widthInches = card?.widthInches;
  const heightInches = card?.heightInches;
  const productType = card?.productType;

  return useMemo(() => {
    const sizeKey = getSupplementaryImageSizeKey({ category, widthInches, heightInches, productType });
    const assets = filterPreviewPaneAssets(getPreviewPaneAssets(tq), occasion, category, vendor, sizeKey);
    return mapAssetsToGalleryAssetObjects(assets);
  }, [tq, occasion, category, vendor, widthInches, heightInches, productType]);
};
